/** Focus containment for a dialog: trap while open, Escape to leave, focus back to the trigger. */

import { useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Trap the tab ring inside the returned element while `open`, and call `onEscape` on Escape.
 * The callback is held in a ref so an inline arrow does not re-run the effect and steal focus
 * back on every render.
 */
export function useFocusTrap<T extends HTMLElement>(open: boolean, onEscape?: () => void) {
  const container = useRef<T | null>(null);
  const escape = useRef<(() => void) | undefined>(onEscape);

  useEffect(() => {
    escape.current = onEscape;
  });

  useEffect(() => {
    if (!open) return;
    const node = container.current;
    if (!node) return;
    const restoreTo = document.activeElement as HTMLElement | null;

    const focusable = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.getAttribute("aria-hidden") !== "true",
      );

    const opening = focusable()[0];
    if (opening) opening.focus();
    else node.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        escape.current?.();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        event.preventDefault();
        node.focus();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (restoreTo && typeof restoreTo.focus === "function") restoreTo.focus();
    };
  }, [open]);

  return container;
}
